import React, { useCallback, useEffect, useState } from 'react';
import Modal from '@mui/material/Modal';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigationRoundedIcon from '@mui/icons-material/NavigationRounded';
import { getRequest, postRequest, putRequest, deleteRequest, showMessage } from '../../../config';
import ImageUploader from '../modules/ModuleForm/ImageUploader';

const STAGE_OPTS = ['Investment', 'Growth', 'Exit'];
const SUB_OPTS = ['Free', 'Lite', 'Pro'];
const TARGET_TAB_OPTS = ['Dashboard', 'AnyLists', 'ResearchAid', 'Workflows'];

const createStageSubscriptionConfig = () => {
    const list = [];
    STAGE_OPTS.forEach((stage) => {
        SUB_OPTS.forEach((sub) => {
            list.push({
                companyStage: stage,
                companySubscription: sub,
                isVisible: true,
                isRestricted: false,
            });
        });
    });
    return list;
};

const normalizeStageSubscriptionConfig = (raw) => {
    const source = Array.isArray(raw) ? raw : [];
    if (!source.length) {
        return createStageSubscriptionConfig();
    }
    const map = new Map();
    source.forEach((item) => {
        if (!item) return;
        const key = `${item.companyStage}__${item.companySubscription}`;
        map.set(key, {
            companyStage: item.companyStage,
            companySubscription: item.companySubscription,
            isVisible: item.isVisible !== false,
            isRestricted: item.isRestricted === true,
        });
    });
    return createStageSubscriptionConfig().map((entry) => {
        const key = `${entry.companyStage}__${entry.companySubscription}`;
        return map.get(key) || entry;
    });
};

const emptyForm = () => ({
    dashboardCardId: '',
    title: '',
    subTitle: '',
    description: '',
    imageURL: '',
    companyStage: 'All',
    companySubscription: 'All',
    stageSubscriptionConfig: createStageSubscriptionConfig(),
    targetTab: '',
    externalUrl: '',
    isActive: true,
});

const NavigationModulePage = () => {
    const [items, setItems] = useState([]);
    const [dashboardCards, setDashboardCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [navRes, dashRes] = await Promise.all([
                getRequest('/modules/navigation-module-cards'),
                getRequest('/modules/dashboard-cards'),
            ]);
            if (navRes?.code === 200 && navRes?.body?.items) {
                setItems(navRes.body.items);
            } else {
                setItems([]);
            }
            if (dashRes?.code === 200 && dashRes?.body?.cards) {
                setDashboardCards(dashRes.body.cards);
            } else {
                setDashboardCards([]);
            }
        } catch (e) {
            console.error(e);
            showMessage('Could not load navigation modules.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            dashboardCardId: row.dashboardCardId?._id || row.dashboardCardId || '',
            title: row.title || '',
            subTitle: row.subTitle || '',
            description: row.description || '',
            imageURL: row.imageURL || '',
            companyStage: row.companyStage || 'All',
            companySubscription: row.companySubscription || 'All',
            targetTab: row.targetTab || '',
            externalUrl: row.externalUrl || '',
            stageSubscriptionConfig: normalizeStageSubscriptionConfig(row.stageSubscriptionConfig),
            isActive: row.isActive !== false,
        });
        setModalOpen(true);
    };

    const handleClose = () => {
        setModalOpen(false);
        setEditing(null);
        setForm(emptyForm());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.dashboardCardId || !form.title?.trim() || !form.targetTab?.trim()) {
            showMessage('Dashboard card, title, and target tab are required.');
            return;
        }
        const payload = {
            dashboardCardId: form.dashboardCardId,
            title: form.title.trim(),
            subTitle: (form.subTitle || '').trim(),
            description: (form.description || '').trim(),
            imageURL: (form.imageURL || '').trim(),
            companyStage: 'All',
            companySubscription: 'All',
            stageSubscriptionConfig: normalizeStageSubscriptionConfig(form.stageSubscriptionConfig),
            targetTab: form.targetTab.trim(),
            externalUrl: (form.externalUrl || '').trim(),
            isActive: form.isActive,
        };
        if (editing?._id) payload._id = editing._id;

        try {
            const res = editing?._id
                ? await putRequest('/modules/navigation-module-cards', payload)
                : await postRequest('/modules/navigation-module-cards', payload);
            if (res?.code === 200) {
                showMessage(editing ? 'Navigation entry updated.' : 'Navigation entry created.');
                handleClose();
                load();
            } else {
                showMessage(res?.message || 'Save failed');
            }
        } catch (err) {
            console.error(err);
            showMessage('Save failed.');
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete "${row.title}"?`)) return;
        try {
            const res = await deleteRequest(`/modules/navigation-module-cards/${row._id}`);
            if (res?.code === 200) {
                showMessage('Deleted.');
                load();
            } else {
                showMessage(res?.message || 'Delete failed');
            }
        } catch (e) {
            console.error(e);
            showMessage('Delete failed.');
        }
    };

    const dashTitle = (id) => {
        const c = dashboardCards.find((x) => String(x._id) === String(id));
        return c?.title || '—';
    };

    const getVariantCell = (stage, sub) => {
        const list = Array.isArray(form.stageSubscriptionConfig) ? form.stageSubscriptionConfig : [];
        return list.find((x) => x.companyStage === stage && x.companySubscription === sub) || {
            companyStage: stage,
            companySubscription: sub,
            isVisible: false,
            isRestricted: false,
        };
    };

    const updateVariantCell = (stage, sub, patch) => {
        setForm((prev) => {
            const next = normalizeStageSubscriptionConfig(prev.stageSubscriptionConfig).map((x) => {
                if (x.companyStage !== stage || x.companySubscription !== sub) return x;
                return { ...x, ...patch };
            });
            return { ...prev, stageSubscriptionConfig: next };
        });
    };

    const updateAllCells = (patch) => {
        setForm((prev) => ({
            ...prev,
            stageSubscriptionConfig: normalizeStageSubscriptionConfig(prev.stageSubscriptionConfig).map((x) => ({ ...x, ...patch })),
        }));
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 14px',
        fontSize: '0.95rem',
        border: '1px solid #e0e0e0',
        borderRadius: '10px',
        outline: 'none',
        boxSizing: 'border-box',
        background: '#fff',
    };

    const labelStyle = { display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.8rem', color: '#1a2744' };

    return (
        <div className="modules-container" style={{ maxWidth: '1200px' }}>
            <div
                className="nav-module-hero df fdc"
                style={{
                    borderRadius: '20px',
                    padding: '2rem 2.25rem',
                    marginBottom: '1.75rem',
                    background: 'linear-gradient(135deg, #0b1f63 0%, #1d4ed8 50%, #2563eb 100%)',
                    color: '#f8fafc',
                    boxShadow: '0 24px 48px rgba(37, 99, 235, 0.28)',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'radial-gradient(circle at 85% 20%, rgba(255,255,255,0.12) 0%, transparent 45%)',
                        pointerEvents: 'none',
                    }}
                />
                <div className="df aic jcsb" style={{ position: 'relative', zIndex: 1, flexWrap: 'wrap', gap: '1rem' }}>
                    <div className="df aic" style={{ gap: '1rem' }}>
                        <div
                            className="df aic jcc"
                            style={{
                                width: 52,
                                height: 52,
                                borderRadius: 14,
                                background: 'rgba(59,130,246,0.18)',
                                border: '1px solid rgba(147,197,253,0.55)',
                            }}
                        >
                            <NavigationRoundedIcon style={{ fontSize: 30, color: '#60a5fa' }} />
                        </div>
                        <div>
                            <h1 className="modules-title" style={{ color: '#fff', margin: 0, fontSize: '1.65rem', letterSpacing: '-0.02em' }}>
                                Navigation Module
                            </h1>
                            <p style={{ margin: '0.35rem 0 0', opacity: 0.88, fontSize: '0.95rem', maxWidth: '520px', lineHeight: 1.5 }}>
                                Curate shortcut tiles that appear on the company dashboard next to checklist modules. Each entry links to an in-app tab or an external URL.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="df aic jcc cp"
                        style={{
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px 20px',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            background: '#bfdbfe',
                            color: '#1e3a8a',
                            boxShadow: '0 8px 24px rgba(191, 219, 254, 0.35)',
                        }}
                    >
                        <AddIcon style={{ fontSize: 20, marginRight: 8 }} />
                        New navigation card
                    </button>
                </div>
            </div>

            <div
                className="modules-table-card"
                style={{
                    borderRadius: '16px',
                    border: '1px solid #e8edf5',
                    background: '#fafbfd',
                    padding: '1.25rem 1.5rem',
                }}
            >
                <div className="df jcsb aic mb1rem" style={{ flexWrap: 'wrap', gap: '8px' }}>
                    <h3 className="modules-table-title" style={{ margin: 0 }}>
                        Entries ({items.length})
                    </h3>
                </div>

                {loading ? (
                    <div className="df jcc aic p4rem">
                        <div className="loader" />
                        <span className="ml1rem fs0-75rem c15">Loading…</span>
                    </div>
                ) : items.length === 0 ? (
                    <div
                        className="tac py4rem"
                        style={{
                            borderRadius: '12px',
                            border: '1px dashed #c7d2fe',
                            background: '#fff',
                            color: '#64748b',
                        }}
                    >
                        <p style={{ margin: 0, fontSize: '1rem' }}>No navigation cards yet. Create one to show it on the dashboard.</p>
                    </div>
                ) : (
                    <div className="dg" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                        {items.map((row) => (
                            <div
                                key={row._id}
                                className="bsbb"
                                style={{
                                    background: '#fff',
                                    borderRadius: '14px',
                                    border: '1px solid #e8edf5',
                                    padding: '1.1rem 1.25rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '0.65rem',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 12px 28px rgba(15, 23, 42, 0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'none';
                                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(15, 23, 42, 0.06)';
                                }}
                            >
                                <div className="df aifs jcsb" style={{ gap: '8px' }}>
                                    <div className="df aic" style={{ gap: '10px', minWidth: 0 }}>
                                        <div
                                            className="df aic jcc"
                                            style={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 10,
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                                border: '1px solid #e8edf5',
                                                background: '#f1f5f9',
                                            }}
                                        >
                                            {row.imageURL ? (
                                                <img src={row.imageURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <NavigationRoundedIcon style={{ color: '#94a3b8', fontSize: 26 }} />
                                            )}
                                        </div>
                                        <div style={{ minWidth: 0 }}>
                                            <div className="fw600 c15 fs0-95rem" style={{ lineHeight: 1.3 }}>
                                                {row.title}
                                            </div>
                                            {row.subTitle ? (
                                                <div className="fs0-8rem c15 op70" style={{ marginTop: 2 }}>
                                                    {row.subTitle}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="df aic">
                                        <button
                                            type="button"
                                            className="df aic jcc cp bg44 c12"
                                            style={{ padding: 6, marginRight: 4 }}
                                            title="Edit"
                                            onClick={() => openEdit(row)}
                                        >
                                            <EditIcon style={{ fontSize: 20 }} />
                                        </button>
                                        <button
                                            type="button"
                                            className="df aic jcc cp"
                                            style={{ padding: 6, background: 'transparent', border: 'none' }}
                                            title="Delete"
                                            onClick={() => handleDelete(row)}
                                        >
                                            <DeleteIcon style={{ fontSize: 20, color: '#b91c1c' }} />
                                        </button>
                                    </div>
                                </div>
                                <div className="fs0-75rem c15 op85" style={{ lineHeight: 1.45 }}>
                                    <span style={{ fontWeight: 600 }}>Dashboard: </span>
                                    {dashTitle(row.dashboardCardId?._id || row.dashboardCardId)}
                                </div>
                                <div className="df fww aic" style={{ gap: '6px' }}>
                                    <span
                                        style={{
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            padding: '3px 8px',
                                            borderRadius: '6px',
                                            background: '#ecfdf5',
                                            color: '#047857',
                                        }}
                                    >
                                        Tab: {row.targetTab}
                                    </span>
                                    {row.externalUrl ? (
                                        <span className="df aic" style={{ fontSize: '0.7rem', color: '#2563eb', gap: 2 }}>
                                            <OpenInNewIcon style={{ fontSize: 14 }} /> External
                                        </span>
                                    ) : null}
                                    {!row.isActive ? (
                                        <span
                                            style={{
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                padding: '3px 8px',
                                                borderRadius: '6px',
                                                background: '#fef2f2',
                                                color: '#b91c1c',
                                            }}
                                        >
                                            Inactive
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Modal open={modalOpen} onClose={handleClose} className="backdrop td500 zi4 df jcc aic">
                <div
                    className="bg1 shadow br15px bsbb"
                    style={{
                        outline: 'none',
                        maxWidth: '520px',
                        width: '92%',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        padding: '1.75rem',
                        position: 'relative',
                    }}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="pa cp"
                        style={{ top: 12, right: 12, border: 'none', background: 'transparent' }}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </button>
                    <h2 className="fs1-25rem fw600 c15 mb1rem" style={{ paddingRight: 32 }}>
                        {editing ? 'Edit navigation card' : 'Create navigation card'}
                    </h2>
                    <form onSubmit={handleSubmit}>
                        <label style={labelStyle}>Dashboard card</label>
                        <select
                            style={{ ...inputStyle, marginBottom: 14 }}
                            value={form.dashboardCardId}
                            onChange={(e) => setForm((f) => ({ ...f, dashboardCardId: e.target.value }))}
                            required
                        >
                            <option value="">Select dashboard section…</option>
                            {dashboardCards.map((c) => (
                                <option key={c._id} value={c._id}>
                                    {c.title || c._id}
                                </option>
                            ))}
                        </select>

                        <label style={labelStyle}>Title</label>
                        <input
                            style={{ ...inputStyle, marginBottom: 14 }}
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            required
                        />

                        <label style={labelStyle}>Subtitle</label>
                        <input style={{ ...inputStyle, marginBottom: 14 }} value={form.subTitle} onChange={(e) => setForm((f) => ({ ...f, subTitle: e.target.value }))} />

                        <label style={labelStyle}>Description</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: 72, resize: 'vertical', marginBottom: 14 }}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />

                        <div style={{ marginBottom: 14 }}>
                            <ImageUploader
                                imageURL={form.imageURL}
                                onImageChange={(url) => setForm((f) => ({ ...f, imageURL: url }))}
                                label="Navigation card image"
                            />
                        </div>

                        <div style={{ marginBottom: 14 }}>
                            <label style={labelStyle}>Active</label>
                            <select
                                style={inputStyle}
                                value={form.isActive ? 'yes' : 'no'}
                                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.value === 'yes' }))}
                            >
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: 18 }}>
                            <label style={labelStyle}>Visibility Matrix (easy mode)</label>


                            <div className="df fdc" style={{ gap: 10 }}>
                                {STAGE_OPTS.map((stage) => (
                                    <div key={stage} style={{ border: '1px solid #e6edf6', borderRadius: 10, padding: 10, background: '#fbfdff' }}>
                                        <div className="fw600 fs0-85rem mb0-5rem" style={{ color: '#334155' }}>{stage}</div>
                                        <div className="dg" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                                            {SUB_OPTS.map((sub) => {
                                                const cell = getVariantCell(stage, sub);
                                                return (
                                                    <div key={`${stage}-${sub}`} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, background: '#fff' }}>
                                                        <div className="fw600 fs0-75rem mb0-5rem">{sub}</div>
                                                        <label className="df aic" style={{ gap: 6, marginBottom: 6 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={cell.isVisible === true}
                                                                onChange={(e) => updateVariantCell(stage, sub, { isVisible: e.target.checked })}
                                                            />
                                                            <span className="fs0-75rem">is Visible?</span>
                                                        </label>
                                                        <label className="df aic" style={{ gap: 6 }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={cell.isRestricted === true}
                                                                onChange={(e) => updateVariantCell(stage, sub, { isRestricted: e.target.checked })}
                                                            />
                                                            <span className="fs0-75rem">is Restricted?</span>
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <small className="fs11px c15 op60 mt0-5rem">
                                Turn visibility on/off and mark combinations as restricted.
                            </small>
                        </div>

                        <label style={labelStyle}>Target tab</label>
                        <select
                            style={{ ...inputStyle, marginBottom: 14 }}
                            value={form.targetTab}
                            onChange={(e) => setForm((f) => ({ ...f, targetTab: e.target.value }))}
                            required
                        >
                            <option value="">Select target tab...</option>
                            {TARGET_TAB_OPTS.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>

                        <label style={labelStyle}>External URL (optional — opens in new tab)</label>
                        <input
                            style={{ ...inputStyle, marginBottom: 20 }}
                            value={form.externalUrl}
                            onChange={(e) => setForm((f) => ({ ...f, externalUrl: e.target.value }))}
                            placeholder="https://…"
                        />

                        <div className="df jcfe" style={{ gap: 10 }}>
                            <button type="button" className="cp p1rem br8px bg44" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="cp p1rem br8px c1 fw600"
                                style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', border: 'none' }}
                            >
                                {editing ? 'Save changes' : 'Create'}
                            </button>
                        </div>
                    </form>
                </div>
            </Modal>
        </div>
    );
};

export default NavigationModulePage;
